import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonText,
  IonSpinner,
  IonIcon,
  IonModal,
  IonSearchbar,
  IonAvatar,
  IonFooter,
  IonInput,
  IonChip,
  IonActionSheet,
  IonToast,
  IonAlert,
} from '@ionic/react';
import PhoneInputField from '../components/PhoneInputField';
import { isPhoneOptionalValid, phoneCountryFromLocale } from '../lib/phone';
import {
  addOutline,
  personOutline,
  chatbubbleOutline,
  linkOutline,
  mailOutline,
  callOutline,
  logoWhatsapp,
  copyOutline,
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { apiUrl, apiHeaders } from '../lib/api';
import { useI18n } from '../i18n/I18nProvider';
import {
  findExistingDirectChatsForUser,
  startChatFromContact,
  upsertContactFromIdentity,
  type DirectChatRef,
  type StartChatContact,
  type StartChatResult,
} from '../lib/startChatFlow';

interface DirectChatSummary {
  id: string;
  name: string;
  type: string;
  createdAt: string;
  updatedAt: string;
  participantUserId: string | null;
}

type ContactData = StartChatContact & {
  linkedUser: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    imageUrl: string | null;
  } | null;
};

const ChatsPage: React.FC = () => {
  const history = useHistory();
  const { getToken } = useAuth();
  const { t, locale } = useI18n();
  const [phoneForceError, setPhoneForceError] = useState(false);

  const [chats, setChats] = useState<DirectChatSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showPicker, setShowPicker] = useState(false);
  const [contacts, setContacts] = useState<ContactData[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [startingKey, setStartingKey] = useState<string | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);

  const [quickName, setQuickName] = useState('');
  const [quickEmail, setQuickEmail] = useState('');
  const [quickPhone, setQuickPhone] = useState('');

  const [inviteNeeded, setInviteNeeded] = useState<Extract<
    StartChatResult,
    { status: 'invite_needed' }
  > | null>(null);
  const [showInviteSheet, setShowInviteSheet] = useState(false);
  const [copiedFeedback, setCopiedFeedback] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [existingChatPrompt, setExistingChatPrompt] = useState<{
    existing: DirectChatRef[];
    resume: (forceNew: boolean) => Promise<void>;
  } | null>(null);

  const formatChatCreatedAt = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  };

  const loadChats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const res = await fetch(apiUrl('/api/chat/conversations'), {
        headers: {
          'Content-Type': 'application/json',
          ...apiHeaders(token),
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to load chats (${res.status})`);
      }

      const data = (await res.json()) as DirectChatSummary[];
      setChats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('chats.error.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [getToken, t]);

  const loadContacts = useCallback(async () => {
    try {
      setContactsLoading(true);
      setPickerError(null);
      const token = await getToken();
      const res = await fetch(apiUrl('/api/contacts'), {
        headers: {
          'Content-Type': 'application/json',
          ...apiHeaders(token),
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to load contacts (${res.status})`);
      }

      const data = (await res.json()) as ContactData[];
      setContacts(data);
    } catch (err) {
      setPickerError(
        err instanceof Error ? err.message : t('contacts.error.loadFailed')
      );
    } finally {
      setContactsLoading(false);
    }
  }, [getToken, t]);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  const filteredContacts = useMemo(() => {
    if (!contactSearch.trim()) return contacts;
    const q = contactSearch.toLowerCase();
    return contacts.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.emails.some((e) => e.toLowerCase().includes(q)) ||
        c.phones.some((p) => p.includes(q))
    );
  }, [contacts, contactSearch]);

  const handleOpenChat = (conversationId: string) => {
    history.push(`/chat/${conversationId}`);
  };

  const resetQuickForm = () => {
    setQuickName('');
    setQuickEmail('');
    setQuickPhone('');
  };

  const handleOpenPicker = () => {
    setContactSearch('');
    setPickerError(null);
    resetQuickForm();
    setInviteNeeded(null);
    setShowPicker(true);
    void loadContacts();
  };

  const syncContactInList = (contact: StartChatContact | ContactData) => {
    setContacts((prev) => {
      const next = prev.some((c) => c.id === contact.id)
        ? prev.map((c) =>
            c.id === contact.id ? { ...c, ...contact } : c
          )
        : [...prev, contact as ContactData];
      return next.sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  const applyStartResult = (result: StartChatResult) => {
    syncContactInList(result.contact);
    if (result.contactCreated) {
      setToastMessage(
        t('chats.modal.quick.contactSaved', { name: result.contact.name })
      );
    }
    if (result.status === 'ready') {
      setShowPicker(false);
      resetQuickForm();
      history.push(`/chat/${result.conversationId}`);
      return;
    }
    setInviteNeeded(result);
    setShowInviteSheet(true);
  };

  const runStartDirectChat = async (
    contact: ContactData,
    forceNew: boolean
  ) => {
    try {
      setStartingKey(contact.id);
      setPickerError(null);
      const token = await getToken();
      const result = await startChatFromContact(token, contact, false, {
        forceNew,
      });
      applyStartResult(result);
      if (forceNew) void loadChats();
    } catch (err) {
      setPickerError(
        err instanceof Error ? err.message : t('chats.error.createFailed')
      );
    } finally {
      setStartingKey(null);
    }
  };

  const handleStartDirectChat = async (contact: ContactData) => {
    if (startingKey) return;
    if (contact.linkedUserId) {
      const existing = findExistingDirectChatsForUser(
        chats,
        contact.linkedUserId
      );
      if (existing.length > 0) {
        setExistingChatPrompt({
          existing,
          resume: (forceNew) => runStartDirectChat(contact, forceNew),
        });
        return;
      }
    }
    await runStartDirectChat(contact, false);
  };

  const handleQuickStart = async () => {
    if (startingKey) return;
    const email = quickEmail.trim();
    const phone = quickPhone.trim();
    if (!email && !phone) {
      setPickerError(t('chats.modal.quick.identityRequired'));
      return;
    }
    if (
      phone &&
      !isPhoneOptionalValid(phone, phoneCountryFromLocale(locale))
    ) {
      setPhoneForceError(true);
      setPickerError(t('phone.invalid'));
      return;
    }
    // Resolve contact first so we can detect an existing 1:1 before creating.
    try {
      setStartingKey('quick');
      setPickerError(null);
      setPhoneForceError(false);
      const token = await getToken();
      const { contact, created } = await upsertContactFromIdentity(
        token,
        { name: quickName.trim(), email, phone, locale },
        contacts
      );
      syncContactInList(contact);
      if (created) {
        setToastMessage(
          t('chats.modal.quick.contactSaved', { name: contact.name })
        );
      }
      if (contact.linkedUserId) {
        const existing = findExistingDirectChatsForUser(
          chats,
          contact.linkedUserId
        );
        if (existing.length > 0) {
          setStartingKey(null);
          setExistingChatPrompt({
            existing,
            resume: async (forceNew) => {
              try {
                setStartingKey('quick');
                const result = await startChatFromContact(
                  token,
                  contact,
                  false,
                  { forceNew }
                );
                applyStartResult(result);
                if (forceNew) void loadChats();
              } catch (err) {
                setPickerError(
                  err instanceof Error
                    ? err.message
                    : t('chats.error.createFailed')
                );
              } finally {
                setStartingKey(null);
              }
            },
          });
          return;
        }
      }
      const result = await startChatFromContact(token, contact, created);
      applyStartResult(result);
    } catch (err) {
      if (err instanceof Error && err.message === 'EMAIL_OR_PHONE_REQUIRED') {
        setPickerError(t('chats.modal.quick.identityRequired'));
      } else if (err instanceof Error && err.message === 'INVALID_PHONE') {
        setPhoneForceError(true);
        setPickerError(t('phone.invalid'));
      } else {
        setPickerError(
          err instanceof Error ? err.message : t('chats.error.createFailed')
        );
      }
    } finally {
      setStartingKey(null);
    }
  };

  const handleCopyInvite = async () => {
    if (!inviteNeeded) return;
    try {
      await navigator.clipboard.writeText(inviteNeeded.joinUrl);
      setCopiedFeedback(true);
      setTimeout(() => setCopiedFeedback(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton
              defaultHref="/dashboard"
              text={t('common.actions.back')}
            />
          </IonButtons>
          <IonTitle>{t('chats.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={handleOpenPicker}
              aria-label={t('chats.newDirectChat')}
            >
              <IonIcon slot="icon-only" icon={addOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="ion-padding vault-screen">
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && !loading && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}

        {!loading && !error && chats.length === 0 && (
          <div className="ion-text-center ion-padding">
            <IonText color="medium">
              <p>{t('chats.empty')}</p>
            </IonText>
            <IonButton fill="outline" onClick={handleOpenPicker}>
              <IonIcon slot="start" icon={chatbubbleOutline} />
              {t('chats.newDirectChat')}
            </IonButton>
          </div>
        )}

        <IonList className="vault-feed-list">
          {chats.map((chat) => (
            <IonItem
              key={chat.id}
              button
              detail
              lines="none"
              className="vault-feed-item"
              onClick={() => handleOpenChat(chat.id)}
            >
              <IonLabel>
                <h2>{chat.name}</h2>
                <p>
                  <small>{t('chats.directLabel')}</small>
                  {' · '}
                  <small>
                    {t('chats.createdAt', {
                      date: formatChatCreatedAt(chat.createdAt),
                    })}
                  </small>
                </p>
              </IonLabel>
            </IonItem>
          ))}
        </IonList>
      </IonContent>

      <IonModal
        className="vault-glass-modal"
        isOpen={showPicker}
        onDidDismiss={() => setShowPicker(false)}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('chats.modal.newDirectChat.title')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowPicker(false)}>
                {t('common.actions.close')}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonText color="medium">
            <p className="chats-quick-hint">{t('chats.modal.quick.hint')}</p>
          </IonText>

          <IonItem lines="none" className="vault-field-item">
            <IonLabel position="stacked">{t('chats.modal.quick.name')}</IonLabel>
            <IonInput
              value={quickName}
              onIonInput={(e) => setQuickName(e.detail.value ?? '')}
              placeholder={t('chats.modal.quick.namePlaceholder')}
            />
          </IonItem>
          <IonItem lines="none" className="vault-field-item">
            <IonLabel position="stacked">{t('chats.modal.quick.email')}</IonLabel>
            <IonInput
              type="email"
              inputMode="email"
              autocomplete="email"
              value={quickEmail}
              onIonInput={(e) => setQuickEmail(e.detail.value ?? '')}
              placeholder={t('chats.modal.quick.emailPlaceholder')}
            />
          </IonItem>
          <PhoneInputField
            value={quickPhone}
            onChange={setQuickPhone}
            label={t('chats.modal.quick.phone')}
            placeholder={t('chats.modal.quick.phonePlaceholder')}
            showError={phoneForceError}
          />
          <IonText color="medium">
            <p className="chats-quick-hint chats-quick-hint--note">
              {t('chats.modal.quick.createContactNote')}
            </p>
          </IonText>
          <IonButton
            expand="block"
            className="ion-margin-vertical"
            disabled={
              startingKey === 'quick' ||
              (!quickEmail.trim() && !quickPhone.trim())
            }
            onClick={() => void handleQuickStart()}
          >
            {startingKey === 'quick' ? (
              <IonSpinner name="crescent" />
            ) : (
              t('chats.modal.quick.start')
            )}
          </IonButton>

          <IonSearchbar
            value={contactSearch}
            onIonInput={(e) => setContactSearch(e.detail.value ?? '')}
            placeholder={t('chats.modal.pickContact.search')}
            debounce={150}
          />

          {contactsLoading && (
            <div className="ion-text-center ion-padding">
              <IonSpinner name="crescent" />
            </div>
          )}

          {pickerError && !contactsLoading && (
            <IonText color="danger">
              <p>{pickerError}</p>
            </IonText>
          )}

          {!contactsLoading && contacts.length === 0 && (
            <div className="ion-text-center ion-padding">
              <IonText color="medium">
                <p>{t('chats.modal.pickContact.emptyAll')}</p>
              </IonText>
            </div>
          )}

          {!contactsLoading && filteredContacts.length > 0 && (
            <IonList className="vault-feed-list contacts-list">
              {filteredContacts.map((contact) => {
                const primaryEmail = contact.emails[0] ?? '';
                const primaryPhone = contact.phones[0] ?? '';
                return (
                  <IonItem
                    key={contact.id}
                    button
                    detail={false}
                    lines="none"
                    className="vault-feed-item contacts-row"
                    disabled={startingKey === contact.id}
                    onClick={() => void handleStartDirectChat(contact)}
                  >
                    <div className="contacts-row-body">
                      <IonAvatar className="contacts-avatar">
                        {contact.linkedUser?.imageUrl ? (
                          <img src={contact.linkedUser.imageUrl} alt="" />
                        ) : (
                          <div className="contacts-avatar-fallback">
                            <IonIcon icon={personOutline} />
                          </div>
                        )}
                      </IonAvatar>
                      <div className="contacts-row-content">
                        <p className="contacts-item-name">{contact.name}</p>
                        {(primaryEmail || primaryPhone) && (
                          <p className="contacts-item-meta">
                            {primaryEmail || primaryPhone}
                          </p>
                        )}
                        <div className="contacts-row-actions">
                          {contact.linkedUserId ? (
                            <IonChip
                              color="success"
                              className="contacts-linked-chip"
                            >
                              <IonIcon icon={linkOutline} />
                              <IonLabel>
                                {t('chats.modal.pickContact.linked')}
                              </IonLabel>
                            </IonChip>
                          ) : (
                            <IonChip
                              color="medium"
                              className="contacts-linked-chip"
                            >
                              <IonLabel>
                                {t('chats.modal.pickContact.invite')}
                              </IonLabel>
                            </IonChip>
                          )}
                          {startingKey === contact.id && (
                            <IonSpinner name="crescent" />
                          )}
                        </div>
                      </div>
                    </div>
                  </IonItem>
                );
              })}
            </IonList>
          )}

          {!contactsLoading &&
            contacts.length > 0 &&
            filteredContacts.length === 0 && (
              <IonText color="medium">
                <p className="ion-padding">
                  {t('chats.modal.pickContact.noResults')}
                </p>
              </IonText>
            )}

          <style>{`
            .chats-quick-hint {
              margin: 0 0 0.5rem;
              font-size: 0.9rem;
              line-height: 1.4;
            }
            .chats-quick-hint--note {
              margin: 0.35rem 0 0;
              font-size: 0.8rem;
            }
          `}</style>
        </IonContent>
        <IonFooter>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => setShowPicker(false)}>
                {t('common.actions.cancel')}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      </IonModal>

      <IonToast
        isOpen={!!toastMessage}
        message={toastMessage ?? ''}
        duration={2200}
        onDidDismiss={() => setToastMessage(null)}
      />

      <IonActionSheet
        isOpen={showInviteSheet && !!inviteNeeded}
        header={t('chats.invite.neededTitle', {
          name: inviteNeeded?.contact.name ?? '',
        })}
        subHeader={
          inviteNeeded?.contactCreated
            ? t('chats.invite.neededBodyCreated')
            : t('chats.invite.neededBody')
        }
        onDidDismiss={() => {
          setShowInviteSheet(false);
        }}
        buttons={[
          ...(inviteNeeded?.contact.emails[0]
            ? [
                {
                  text: t('contacts.invite.email'),
                  icon: mailOutline,
                  handler: () => {
                    const email = inviteNeeded!.contact.emails[0];
                    const subject = encodeURIComponent(
                      t('chats.invite.emailSubject')
                    );
                    const body = encodeURIComponent(inviteNeeded!.shareText);
                    window.open(
                      `mailto:${email}?subject=${subject}&body=${body}`,
                      '_blank'
                    );
                  },
                },
              ]
            : []),
          ...(inviteNeeded?.contact.phones[0]
            ? [
                {
                  text: t('contacts.invite.sms'),
                  icon: callOutline,
                  handler: () => {
                    const phone = inviteNeeded!.contact.phones[0];
                    const body = encodeURIComponent(inviteNeeded!.shareText);
                    window.open(`sms:${phone}?body=${body}`, '_blank');
                  },
                },
              ]
            : []),
          {
            text: t('contacts.invite.whatsapp'),
            icon: logoWhatsapp,
            handler: () => {
              if (!inviteNeeded) return;
              window.open(
                `https://wa.me/?text=${encodeURIComponent(inviteNeeded.shareText)}`,
                '_blank'
              );
            },
          },
          {
            text: copiedFeedback
              ? t('contacts.invite.copied')
              : t('contacts.invite.copy'),
            icon: copyOutline,
            handler: () => {
              void handleCopyInvite();
              return false;
            },
          },
          {
            text: t('common.actions.cancel'),
            role: 'cancel',
          },
        ]}
      />

      <IonAlert
        isOpen={!!existingChatPrompt}
        header={t('chats.existing.title')}
        message={
          existingChatPrompt
            ? existingChatPrompt.existing.length > 1
              ? t('chats.existing.messageMany', {
                  count: existingChatPrompt.existing.length,
                  date: formatChatCreatedAt(
                    existingChatPrompt.existing[0].createdAt
                  ),
                })
              : t('chats.existing.message', {
                  date: formatChatCreatedAt(
                    existingChatPrompt.existing[0].createdAt
                  ),
                })
            : ''
        }
        buttons={[
          {
            text: t('common.actions.cancel'),
            role: 'cancel',
            handler: () => setExistingChatPrompt(null),
          },
          {
            text: t('chats.existing.createNew'),
            handler: () => {
              const prompt = existingChatPrompt;
              setExistingChatPrompt(null);
              if (prompt) void prompt.resume(true);
            },
          },
          {
            text: t('chats.existing.openExisting'),
            handler: () => {
              const id = existingChatPrompt?.existing[0]?.id;
              setExistingChatPrompt(null);
              setShowPicker(false);
              if (id) history.push(`/chat/${id}`);
            },
          },
        ]}
        onDidDismiss={() => setExistingChatPrompt(null)}
      />
    </IonPage>
  );
};

export default ChatsPage;
